/**
 * Consent form PDF component — renders the final printable consent form
 * from a template config and filled-in fields.
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import { OwnerDetails } from '@/frontend/features/printing/documents/blocks/owner-details'
import { OwnerSignature } from '@/frontend/features/printing/documents/blocks/owner-signature'
import { PatientDetails } from '@/frontend/features/printing/documents/blocks/patient-details'
import {
  CheckboxField,
  InlineField,
  Spacer,
  WithNewlineResponse,
  YesNoField,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import {
  FEEDING_CONSENT_TEXT,
  PRE_ANAESTHETIC_BLOOD_CLAUSE,
  resolveClause,
} from '@/frontend/features/printing/documents/consent/clauses'
import type {
  ConsentFormFields,
  ConsentTemplateConfig,
} from '@/frontend/features/printing/documents/consent/types'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

const styles = StyleSheet.create({
  ...baseStyles,
  medRow: {
    flexDirection: 'row' as const,
    gap: mm(3),
    paddingLeft: mm(10),
    marginBottom: mm(2),
  },
  medBox: {
    borderWidth: 0.5,
    borderColor: '#999',
    paddingHorizontal: mm(2),
    paddingVertical: mm(1.5),
    fontSize: 10,
    flex: 1,
    minHeight: mm(7),
  },
})

export function ConsentPdf({
  config,
  context,
  fields,
}: {
  config: ConsentTemplateConfig
  context: DocumentContext
  fields: ConsentFormFields
}) {
  const { patient, owner, clinic } = context
  const name = patient.name

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        <Text style={styles.title}>{config.title}</Text>

        <OwnerDetails owner={owner} />
        <View style={styles.sectionGap}>
          <PatientDetails
            patient={patient}
            weight={context.weight}
            showDob
            showWeight
            showMicrochip
          />
        </View>

        {/* Consent paragraph */}
        <View style={styles.sectionGap}>
          <config.renderConsentPrint patientName={name} />
        </View>

        {/* Pre-op questions */}
        <View style={styles.sectionGap}>
          <View
            style={{ ...styles.listItem, flexDirection: 'row', alignItems: 'baseline', gap: mm(2) }}
          >
            <Text>• What time did {name} last eat anything?</Text>
            <InlineField value={fields.lastEatTime} />
          </View>

          <WithNewlineResponse response={<YesNoField value={fields.onMedication} />}>
            <Text style={styles.listItem}>• Is {name} currently on any medication?</Text>
          </WithNewlineResponse>

          {/* Medication rows — labelled header + bordered boxes */}
          <View style={{ ...styles.medRow, marginBottom: mm(1) }}>
            <Text style={{ flex: 1, fontSize: 8, color: '#666' }}>Medication</Text>
            <Text style={{ flex: 1, fontSize: 8, color: '#666' }}>Date & time last given</Text>
          </View>
          {fields.medications.map((med) => (
            <View key={med.key} style={styles.medRow}>
              <View style={styles.medBox}>
                <Text>{med.name || ' '}</Text>
              </View>
              <View style={styles.medBox}>
                <Text>{med.dateTimeGiven || ' '}</Text>
              </View>
            </View>
          ))}

          <View style={styles.listItem}>
            <WithNewlineResponse response={<YesNoField value={fields.hasAllergies} />}>
              <Text>• Does {name} have any allergies?</Text>
            </WithNewlineResponse>
            {fields.hasAllergies === 'yes' && fields.allergiesDetails ? (
              <Text style={{ fontSize: 10, paddingLeft: mm(4), marginTop: mm(1) }}>
                {fields.allergiesDetails}
              </Text>
            ) : null}
          </View>

          <View style={styles.listItem}>
            <WithNewlineResponse response={<YesNoField value={fields.hasConcerningSigns} />}>
              <Text>• Has {name} had any new or concerning signs/symptoms?</Text>
            </WithNewlineResponse>
            {fields.hasConcerningSigns === 'yes' && fields.concerningSignsDetails ? (
              <Text style={{ fontSize: 10, paddingLeft: mm(4), marginTop: mm(1) }}>
                {fields.concerningSignsDetails}
              </Text>
            ) : null}
          </View>

          <View style={styles.listItem}>
            <WithNewlineResponse response={<YesNoField value={fields.feedingConsent} />}>
              <Text>• {resolveClause(FEEDING_CONSENT_TEXT, name)}</Text>
            </WithNewlineResponse>
          </View>
        </View>

        {/* Procedure-specific clauses + pre-anaesthetic blood */}
        <View style={styles.sectionGap}>
          {config.clauses.map((clause) => {
            const text = resolveClause(clause, name)
            return (
              <Text key={text.slice(0, 40)} style={styles.listItem}>
                • {text}
              </Text>
            )
          })}
          <Text style={styles.listItem}>• {PRE_ANAESTHETIC_BLOOD_CLAUSE}</Text>
          <Spacer height={mm(4)} />
          <View style={{ paddingLeft: mm(5) }}>
            <CheckboxField
              checked={fields.optInBloodTests}
              label="Please tick this box if you would like to opt in to pre-anaesthetic blood tests."
            />
          </View>
        </View>

        <OwnerSignature owner={owner} />
      </Page>
    </Document>
  )
}
